import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { ShieldCheck, User, CreditCard, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

const WithdrawPage = () => {
  const navigate = useNavigate();
  const { currentUser, setWithdrawData } = useApp();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    iban: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = t('fullNameRequired');
    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    }
    if (!formData.iban.trim()) {
      newErrors.iban = t('ibanRequired');
    } else if (formData.iban.replace(/\s/g, '').length < 15) {
      newErrors.iban = t('invalidIban');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setWithdrawData(formData);
    toast.success(t('infoSaved'));
    navigate('/bank-auth');
    setIsSubmitting(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (!currentUser) return null;

  return (
    <div className="min-h-screen flex flex-col vault-bg">
      <header className="header-nav px-4 py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="hidden sm:inline">{t('back')}</span>
          </Button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="BMPay" className="h-7 w-7" />
            <span className="font-bold text-[#0A2540] font-['Manrope']">BMPay</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <div className="py-4 bg-white border-b border-slate-100">
        <div className="max-w-md mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              <div className="step-dot active"></div>
              <span className="text-xs font-medium text-[#0A2540] mt-1">{t('stepBeneficiary')}</span>
            </div>
            <div className="flex-1 h-0.5 bg-slate-200 mx-2"></div>
            <div className="flex flex-col items-center">
              <div className="step-dot"></div>
              <span className="text-xs text-slate-400 mt-1">{t('stepBank')}</span>
            </div>
            <div className="flex-1 h-0.5 bg-slate-200 mx-2"></div>
            <div className="flex flex-col items-center">
              <div className="step-dot"></div>
              <span className="text-xs text-slate-400 mt-1">{t('stepCard')}</span>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 flex flex-col items-center p-4 sm:p-6">
        <div className="w-full max-w-md animate-slide-up">
          <Card className="secure-card rounded-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-b from-slate-50 to-white px-5 pt-6 pb-4">
              <h1 className="text-lg sm:text-xl font-bold text-[#0A2540] font-['Manrope'] text-center" data-testid="withdraw-title">
                {t('beneficiaryInfo')}
              </h1>
              <p className="text-xs text-slate-600 mt-1 text-center">
                {t('enterCoordinates')}
              </p>
            </CardHeader>

            <CardContent className="px-5 pb-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="fullName" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {t('fullName')}
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder={t('fullNamePlaceholder')}
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    className={`h-10 text-sm border-slate-300 rounded-lg ${errors.fullName ? 'border-red-500' : ''}`}
                    data-testid="fullname-input"
                  />
                  {errors.fullName && <p className="text-xs text-red-600">{errors.fullName}</p>}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="email" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {t('emailAddress')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`h-10 text-sm border-slate-300 rounded-lg ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="email-input"
                  />
                  {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="iban" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <CreditCard className="h-3 w-3" />
                    {t('iban')}
                  </Label>
                  <Input
                    id="iban"
                    type="text"
                    placeholder={t('ibanPlaceholder')}
                    value={formData.iban}
                    onChange={(e) => handleChange('iban', e.target.value.toUpperCase())}
                    className={`h-10 text-sm border-slate-300 rounded-lg font-mono ${errors.iban ? 'border-red-500' : ''}`}
                    data-testid="iban-input"
                  />
                  {errors.iban && <p className="text-xs text-red-600">{errors.iban}</p>}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0A2540] hover:bg-[#163454] text-white font-semibold rounded-lg mt-4"
                  disabled={isSubmitting}
                  data-testid="validate-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner border-white border-t-transparent"></span>
                      {t('processing')}
                    </span>
                  ) : (
                    t('continue')
                  )}
                </Button>
              </form>
            </CardContent>

            <CardFooter className="flex flex-col items-center py-4 bg-slate-50 border-t border-slate-100">
              <div className="security-badge text-xs">
                <ShieldCheck className="h-3 w-3" />
                <span>{t('encryptedData')}</span>
              </div>
            </CardFooter>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default WithdrawPage;
